import React from 'react';
import type { OrderListWidgetData, OrderStatus } from '../../types/widgets';

// D-75: unified pill map for the order-list widget. Labels match the
// table in the ADR; colors follow the existing palette (green = positive
// progress, amber = pending action, blue = in transit, red = problem,
// purple = returned, gray = unknown).
const STATUS_PILL: Record<OrderStatus, { label: string; color: string; bg: string }> = {
  placed: { label: 'Order Placed', color: '#059669', bg: '#ECFDF5' },
  processing: { label: 'Processing Order', color: '#D97706', bg: '#FFFBEB' },
  shipped: { label: 'Shipped', color: '#2563EB', bg: '#EFF6FF' },
  out_for_delivery: { label: 'Out For Delivery', color: '#2563EB', bg: '#EFF6FF' },
  delivered: { label: 'Delivered', color: '#059669', bg: '#ECFDF5' },
  cancelled: { label: 'Cancelled', color: '#DC2626', bg: '#FEF2F2' },
  returned: { label: 'Returned', color: '#7C3AED', bg: '#F5F3FF' },
  unknown: { label: 'Unknown', color: '#6B7280', bg: '#F9FAFB' },
};

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
}

function formatAmount(amount: number): string {
  return `$${amount.toFixed(2)}`;
}

export interface OrderListWidgetProps {
  data: OrderListWidgetData;
}

export const OrderListWidget: React.FC<OrderListWidgetProps> = ({ data }) => {
  const { orders, total } = data;
  const extra = total - orders.length;

  return (
    <div
      style={{
        width: '100%',
        maxWidth: 480,
        maxHeight: '100%',
        background: 'var(--voice-widget-card-bg, #FFFFFF)',
        border: '1px solid var(--voice-widget-border, #E5E7EB)',
        borderRadius: 16,
        overflowY: 'auto',
        boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '14px 18px 12px',
          borderBottom: '1px solid var(--voice-widget-border, #E5E7EB)',
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.5,
            textTransform: 'uppercase',
            color: 'var(--voice-widget-muted, #6B7280)',
          }}
        >
          Recent Orders
        </div>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--voice-widget-fg, #111827)', marginTop: 2 }}>
          {total} order{total !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Rows */}
      <div>
        {orders.map((order, i) => {
          const pill = STATUS_PILL[order.status] ?? STATUS_PILL.unknown;
          return (
            <div
              key={order.orderId}
              style={{
                display: 'flex',
                gap: 12,
                alignItems: 'center',
                padding: '10px 18px',
                borderBottom:
                  i < orders.length - 1 ? '1px solid var(--voice-widget-border, #E5E7EB)' : undefined,
              }}
            >
              {/* Lead item thumbnail */}
              {order.leadItem?.imageUrl ? (
                <img
                  src={order.leadItem.imageUrl}
                  alt={order.leadItem.title}
                  style={{
                    width: 40,
                    height: 40,
                    objectFit: 'contain',
                    borderRadius: 6,
                    background: 'var(--voice-widget-img-bg, #F9FAFB)',
                    flexShrink: 0,
                  }}
                />
              ) : (
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 6,
                    background: 'var(--voice-widget-border, #E5E7EB)',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 18,
                    color: 'var(--voice-widget-muted, #6B7280)',
                  }}
                >
                  📦
                </div>
              )}

              {/* Details */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: 'var(--voice-widget-fg, #111827)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {order.leadItem?.title ?? `Order #${order.retailerOrderId ?? order.orderId}`}
                </div>
                <div style={{ fontSize: 11, color: 'var(--voice-widget-muted, #6B7280)', marginTop: 1 }}>
                  {order.itemCount} item{order.itemCount !== 1 ? 's' : ''} · {formatDate(order.orderDate)}
                  {order.retailerName ? ` · ${order.retailerName}` : ''}
                </div>
              </div>

              {/* Right: status + amount */}
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: pill.color,
                    background: pill.bg,
                    padding: '2px 8px',
                    borderRadius: 999,
                    display: 'inline-block',
                    marginBottom: 3,
                  }}
                >
                  {pill.label}
                </div>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--voice-widget-fg, #111827)' }}>
                  {formatAmount(order.totalAmount)}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer: more indicator */}
      {extra > 0 && (
        <div
          style={{
            padding: '8px 18px',
            fontSize: 12,
            color: 'var(--voice-widget-muted, #6B7280)',
            borderTop: '1px solid var(--voice-widget-border, #E5E7EB)',
            textAlign: 'center',
          }}
        >
          +{extra} more order{extra !== 1 ? 's' : ''} — ask me to look up specific ones
        </div>
      )}
    </div>
  );
};

export default OrderListWidget;
